import React, {useEffect, useState, useTransition} from "react";
import {useViewerStore} from "../../../../../../store/store";
import {Button, Spin} from 'antd';
import {useCalculationStates} from "../../../../../../hooks/calculationStateHook";
import {MODULE_GRAPH, siteCalculationState} from "../../../../../../services/siteCalculationState";
import {FloppyDiskIcon} from '@phosphor-icons/react';
import { monthlyEstimateMonths, MonthlyProductionKey } from "../../../../../../Constants";
import { saveMonthlyProductionEstimate } from "../../../../../../services/MultiplierService";
import { useGetSaveDesignData } from "../../../../../../hooks/useGetSaveDesignData";
import { CalculationType } from "../../../../../../@types/enums";
import { MonthlyProductionEstimateProps } from "../../../../../../@types/types";

export const MonthlyProductionEstimate: React.FC<MonthlyProductionEstimateProps> = ({shortened = window.innerWidth <= 1366}) => {

    const [isPending, startTransition] = useTransition();
    const roofInfoArr = useViewerStore(state => state.roofInfoArr);
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const selectedDesignIndex = useViewerStore(state => state.selectedDesignIndex);
    const {needsUpdate} = useCalculationStates([CalculationType.ACCESS_MULTI, CalculationType.IDEAL_MULTI, CalculationType.MODULE_GRAPH]);
    const getPayloadForMonthlyEstimate = useGetSaveDesignData();
    const [isMonthlyEstimateSaveInProgress, setIsMonthlyEstimateSaveInProgress] = useState(false);

    useEffect(() => {
        if (needsUpdate[MODULE_GRAPH]) {
            startTransition(() => {
                siteCalculationState.useCalculations(CalculationType.MODULE_GRAPH);
            });
        }
    }, [needsUpdate]);

    const thArr: { key: MonthlyProductionKey; label: string }[] = [
        { key: "solar_access", label: "Solar Access" },
        { key: "actual_production", label: "Actual Production" },
    ];

    const showAlertOnSave = (message: string) => {
        if(message === ""){ //204 No Content response means success
            sceneViewer.viewerAlert({
                show: true,
                title: "Success",
                message: "Successfully saved Monthly Estimate Data",
                messageType: "success",
                isModal: false,
            });
        }
        else{
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message: message,
                messageType: "error",
                isModal: false,
            });
        }

    }

    const saveMonthlyEstimate = async () => {
        setIsMonthlyEstimateSaveInProgress(true);
        try {
            const payload = await getPayloadForMonthlyEstimate();

            const saveResponse = await saveMonthlyProductionEstimate(payload);
            showAlertOnSave(saveResponse);
        } catch (ex: any) {
            console.error(ex)
            showAlertOnSave(ex);
        } finally {
            setIsMonthlyEstimateSaveInProgress(false);
        }
    }

    return (
        <div className="tab-content-container">
            <Button
                size={'small'}
                loading={isMonthlyEstimateSaveInProgress}
                title={"Save Monthly Production Estimate"}
                icon={<FloppyDiskIcon size={15} />}
                className={"roof-square-table-save-button"}
                type='primary'
                onClick={saveMonthlyEstimate}
                disabled={selectedDesignIndex !== 0}
            >
                Save
            </Button>
            <div className="tab-content-scrollable-container">
                {isPending || Object.values(needsUpdate).includes(true) ? (
                    <>
                        <Spin className="table_spin" />
                        <p>Fetching NREL PV Watts Data...</p>
                    </>
                ) : (
                    <table className="tab__table">
                        <thead>
                            <tr>
                                <th>Section</th>
                                {monthlyEstimateMonths.map((month) => (
                                    <th key={month.short}>{shortened ? month.short : month.long}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {roofInfoArr
                                .filter((roof) => roof.modules > 0)
                                .map((roof, idx) => (
                                <React.Fragment key={`${idx}-${roof.roofFaceId}`}>
                                    <tr key={`building-${idx}`} className="building-item">
                                    <td colSpan={monthlyEstimateMonths.length + 1} style={{ textAlign: "left" }}>
                                        <label>
                                        Roof {roof.roofNum} - Modules: {roof.modules}
                                        </label>
                                    </td>
                                    </tr>
                                    {thArr.map((th) => (
                                    <tr key={`${roof.roofFaceId}-${th.key}`} data-roofid={roof.roofFaceId}>
                                        <td>{th.label}</td>
                                        {monthlyEstimateMonths.map((month) => (
                                        <td key={`${roof.roofFaceId}-${month.short}-${th.key}`} align="center">
                                            {roof.monthlyProductionEstimateData?.[month.long]?.[th.key] !== undefined
                                            ? roof?.monthlyProductionEstimateData[month.long][th.key]?.toFixed(2)
                                            : roof[th.key] !== undefined
                                            ? roof[th.key]
                                            : "-"}
                                        </td>
                                        ))}
                                    </tr>
                                    ))}
                                </React.Fragment>
                                ))
                            }
                            {/* ===== SUMMARY ROWS ===== */}
                            {(() => {
                                // Compute totals for each month (only for actual_production row)
                                const totals = monthlyEstimateMonths.map(({ long }) => {
                                return roofInfoArr
                                    .filter((roof) => roof.modules > 0)
                                    .reduce((sum, roof) => {
                                    const value = roof.monthlyProductionEstimateData?.[long]?.actual_production ?? 0;
                                    return sum + value;
                                    }, 0);
                                });

                                const grandTotal = totals.reduce((sum, val) => sum + val, 0);

                                return (
                                <>
                                    {/* Total row (per-month sum) */}
                                    <tr className="total-row">
                                    <td style={{ fontWeight: "bold" }}>TOTAL</td>
                                    {totals.map((val, i) => (
                                        <td key={`total-${i}`} align="center" style={{ fontWeight: "bold" }}>
                                        {val.toFixed(2)}
                                        </td>
                                    ))}
                                    </tr>

                                    {/* Grand total row (one big number across table) */}
                                    <tr className="grand-total-row">
                                    <td style={{ fontWeight: "bold" }}>TOTAL SYSTEM PRODUCTION</td>
                                    <td colSpan={monthlyEstimateMonths.length} align="center" style={{ fontWeight: "bold" }}>
                                        {grandTotal.toFixed(2)}
                                    </td>
                                    </tr>
                                </>
                                );
                            })()}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default MonthlyProductionEstimate;